/**
 * Get area totals
 * @param  {GenerateAreaDescription}    args
 * @return {string}
 */

import get from 'lodash/get'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import type { QuoteItemSection } from '../../types'
import { getAreaSubstrateDimension } from '../calculate/get-areaSubstrate-dimension'
import { getInchSuffix } from '../calculate/get-inch-suffix'
import { makeHtml } from '../../../../util/makeHtml/makeHtml'
import { getAreaSubstrate } from '../../area-substrates/get-area-substrate'
import { isInSection } from '../../area-substrates/is-in-section'
import { getItemSection } from '../get-item-section'
import { getQuoteOptions } from '../get-options'
import mergeLabels from '../merge-labels'
import { getObjectLabels } from '../../../util/get-object-labels'
import { formatCoatCount, resolveProductCoats } from '../resolve-product-coats'

export function generateAreaDescription(args: {
  quote: QuoteDocument
  options: OptionsDocument
  areaKey: string
  plainText?: boolean
  filterBySection?: QuoteItemSection
  section?: QuoteItemSection
  inGroup?: boolean
}): string {
  const { quote, areaKey, options, section: argsSection, inGroup } = args
  const { hideSubstrateListDescription } = options?.options ?? {}

  const quoteOptions = getQuoteOptions({ quote })

  const area = quote.areas[areaKey]
  const quantities: { [value: string]: number } = {}
  const prices: { [value: string]: number } = {}
  const coats: { [value: string]: number | string } = {}
  const products: { [value: string]: string[] } = {}
  const prep: { [value: string]: number } = {}
  const dimensions: { [value: string]: { value: number; unit: string } } = {}
  const areaReportText: string[] = []

  if (
    !area ||
    !area.substrateOrder ||
    ((quoteOptions.stackSubstrates || quoteOptions.showDetailedBreakdown) && !inGroup)
  ) {
    return '<p></p>'
  }

  const areaSection = getItemSection(area)

  // if no section is given - assume the section the area is in.
  const section = argsSection ?? areaSection

  const areaLabels = area.substrateOrder
    .map((orderItem) => {
      const areaSubstrate = getAreaSubstrate({
        quote,
        areaKey,
        options,
        key: orderItem.key,
        full: false
      })
      if (!areaSubstrate || areaSubstrate.workOrderOnly) {
        return null
      }

      const areaSubstrateSection = areaSubstrate.quoteSection

      if (!isInSection({ areaSubstrateSection, areaSection, section })) {
        return null
      }

      const label = areaSubstrate.clientLabel ? areaSubstrate.clientLabel : areaSubstrate.name ?? ''
      const rateType = get(areaSubstrate, 'rate.rateType')

      // This substrate (label) has been grouped in the substrate view, so it shouldn't be included here.
      if (areaSubstrate.inSubstrateGroup) {
        return null
      }

      // Handle quantities on areaSubstrate
      if (!quantities[label]) {
        quantities[label] = 0
      }
      const quantity =
        typeof areaSubstrate.quantity === 'number' ? areaSubstrate.quantity : Number.parseFloat(areaSubstrate.quantity)
      if (rateType === 'quantity' && !areaSubstrate.hideQuantity) {
        quantities[label] += quantity
      }

      // Handle prices & materials on areaSubstrate
      if (!prices[label]) {
        prices[label] = 0
      }
      const price = areaSubstrate.price.useCustom ? areaSubstrate.price.custom : areaSubstrate.price.default
      const materials = areaSubstrate.materials.useCustom
        ? areaSubstrate.materials.custom
        : areaSubstrate.materials.default

      if (areaSubstrate.showPrice) {
        prices[label] += price + materials
      }

      // Handle products on areaSubstrate
      if (!products[label]) {
        products[label] = []
      }
      const { products: areaSubstrateProducts } = areaSubstrate
      const uniqueAreaSubstrateProducts = uniqBy(areaSubstrateProducts, (item) => item.key)

      // A product may carry its own coats override (see resolveProductCoats). When they disagree
      // and products are shown with coats enabled, fold each product's coats into its own text
      // below instead of summing an ambiguous single number into `coats[label]`.
      const { distinct: distinctSubstrateProductCoats, isMixed: substrateCoatsAreMixed } = resolveProductCoats(
        areaSubstrate.coats,
        uniqueAreaSubstrateProducts
      )
      const foldCoatsIntoProducts =
        substrateCoatsAreMixed && areaSubstrate.showProduct && areaSubstrate.showCoats && areaSubstrate.rate?.useCoats
      const resolvedSubstrateCoats =
        distinctSubstrateProductCoats.length === 1 ? distinctSubstrateProductCoats[0] : areaSubstrate.coats

      for (const areaSubstrateProduct of uniqueAreaSubstrateProducts) {
        const productParts = [areaSubstrateProduct.label, areaSubstrateProduct.color]
        if (foldCoatsIntoProducts) {
          // `||`, not `??` — a product's 0 means "no override". See QuoteProduct.coats.
          productParts.push(formatCoatCount(Number(areaSubstrateProduct.coats || areaSubstrate.coats)))
        }
        const productString = productParts.filter((part) => part).join(' - ')
        if (productString && areaSubstrate.showProduct) {
          products[label].push(productString)
        }
      }

      // Handle dimensions on areaSubstrate
      if (typeof rateType === 'string' && rateType?.includes('ft') && areaSubstrate.showDimensions) {
        const isLnft = rateType === 'lnft'
        const objectLabels = getObjectLabels({ options })
        const isRoom = area.area_type.value === 'room'
        const inchSuffix = getInchSuffix({ useInches: areaSubstrate.useInches, unitValue: objectLabels.unit.value })

        const hasCustomSubstrateDimensions =
          areaSubstrate.length.useCustom || areaSubstrate.width.useCustom || areaSubstrate.height.useCustom

        let dimensionValue: number
        if (hasCustomSubstrateDimensions) {
          const length = areaSubstrate.length.useCustom
            ? areaSubstrate?.length?.custom ?? 0
            : areaSubstrate?.length?.default ?? 0
          const width = areaSubstrate.width.useCustom
            ? areaSubstrate?.width?.custom ?? 0
            : areaSubstrate?.width?.default ?? 0
          const height = areaSubstrate.height.useCustom
            ? areaSubstrate?.height?.custom ?? 0
            : areaSubstrate?.height?.default ?? 0
          dimensionValue = getAreaSubstrateDimension({
            length,
            width,
            height,
            isRoom,
            rateType,
            useInches: areaSubstrate.useInches ?? false
          })
        } else {
          // Fall back to area-level dimensions, same as getDimension in AreaSubstrates.tsx
          const areaDims = area.dimensions
          switch (rateType) {
            case 'lnft':
              dimensionValue = Number(areaDims.lnft) || 0
              break
            case 'sqftWalls':
            case 'sqft_walls':
              dimensionValue = isRoom ? Number(areaDims.sqft_walls) || 0 : Number(areaDims.sqft) || 0
              break
            case 'sqftFloor':
            case 'sqft_floor':
              dimensionValue = isRoom ? Number(areaDims.sqft_floor) || 0 : Number(areaDims.sqft) || 0
              break
            case 'sqftCeiling':
            case 'sqft_ceiling':
              dimensionValue = isRoom ? Number(areaDims.sqft_ceiling) || 0 : Number(areaDims.sqft) || 0
              break
            default:
              dimensionValue = Number(areaDims.sqft) || 0
              break
          }
        }

        dimensions[label] = {
          value: (dimensions?.[label]?.value ?? 0) + dimensionValue,
          unit: `${isLnft ? objectLabels.unit.abbreviation.linear : objectLabels.unit.abbreviation.square}${inchSuffix}`
        }
      }
      // Handle coats on areaSubstrate. When the products disagree there's no single correct number
      // to sum into `coats[label]`: if products are shown, their coats are folded into
      // `products[label]` above instead; if hidden, show the distinct coat counts as a joined
      // list (e.g. "1, 3") rather than an ambiguous single number.
      if (areaSubstrate.showCoats && areaSubstrate.rate?.useCoats) {
        if (substrateCoatsAreMixed) {
          if (!areaSubstrate.showProduct) {
            const mixedCoatsLabel = distinctSubstrateProductCoats.join(', ')
            coats[label] = coats[label] ? `${coats[label]}, ${mixedCoatsLabel}` : mixedCoatsLabel
          }
        } else {
          // A prior substrate on this label may have already written a joined mixed-coats string
          // above; append to it instead of resetting to 0 and silently dropping it.
          const numericContribution = resolvedSubstrateCoats ?? 0
          coats[label] =
            typeof coats[label] === 'string'
              ? `${coats[label]}, ${numericContribution}`
              : (coats[label] ?? 0) + numericContribution
        }
      }

      // Handle prep on areaSubstrate
      if (areaSubstrate.showPrep && areaSubstrate.prep) {
        prep[label] = (prep?.[label] ?? 0) + (areaSubstrate?.prep ?? 0)
      }

      if (areaSubstrate.rate.areaReportText) {
        areaReportText.push(areaSubstrate.rate.areaReportText)
      }

      // Hide the "substrate list" part of the description if the option is set
      // but still check if there is an area view description
      if (hideSubstrateListDescription) {
        return null
      }

      return label
    })
    .filter((item) => item)

  // Combine labels to create description
  const descriptionElements = mergeLabels({
    options,
    areaLabels,
    quantities,
    dimensions,
    prices,
    coats,
    prep,
    products
  })

  const descriptionFirstLine = uniq(descriptionElements)
    .map((item) => item.trim())
    .join(quoteOptions.stackSubstrates || quoteOptions.showDetailedBreakdown ? '<br />' : ', ')
  const firstLine = makeHtml(descriptionFirstLine)

  const reportText = uniq(areaReportText.map((item) => makeHtml(item)))
    .filter((item) => item)
    .join('')

  if (!firstLine && !reportText) {
    return '<p></p>'
  }

  return `${firstLine}${reportText}`
}
